import { entries, fromPairs, get, set } from 'lodash'
import { DateTime } from 'luxon'

/**
 * Gets a person's full name.
 *
 * @param firstName person's first name.
 * @param lastName person's last name.
 * @returns person's full name, or undefined if first and last are missing.
 */
export function getFullName(firstName: string | null, lastName: string | null) {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`
  } else if (firstName) {
    return firstName
  } else if (lastName) {
    return lastName
  }
}

/**
 * Displays last N chars of a string value prefixed with an ellipsis.
 *
 * @param {string} value value to slice.
 * @param {number} n last N chars to retain.
 * @returns {string} last N chars of value ("...value") or "value" depending on provided N.
 */
export function lastNChars(value: string, n: number): string {
  return value.length > n ? `...${value.slice(-n)}` : value
}

/**
 * Turs a base array of string values into a dropdown-options compatible array
 *
 * @param sourceValues array of string values
 * @returns { label, value }-formatted array, with both set to the same string value
 */
export const toDropdownOptions = (sourceValues: string[]) => sourceValues.map((v) => ({ label: v, value: v }))

/**
 * Applies an operation `fn` in-place (mutating) to a list of lodash-style keys within an object.
 * Please type the source and destination object types when using.
 * This is a higher order function. Implement partially with `fieldPaths` and `fn` and call the results upon target objects.
 * @param fieldPaths the list of field paths (lodash-style, e.g. 'a.b[0].c').
 * @param fn the function to apply to the fields. It should take the source value and return the destination value.
 * @returns a function that applies the rectifying operation to an object.
 */
export const rectifyFieldsInPlace =
  <SourceT, DestT>(fieldPaths: string[], fn: (datum: SourceT) => DestT) =>
  (object: any) => {
    fieldPaths.forEach((path) => {
      const sourceValue = get(object, path)
      set(object, path, fn(sourceValue))
    })
  }

/**
 * Returns an object holding key-value pairs for which the key is set to true on the mask
 * @param form the form to mask
 * @param mask the mask object
 */
export const maskForm = (form: Record<string, unknown>, mask: Record<string, boolean>) => {
  return fromPairs(entries(form).filter(([k]) => mask[k]))
}

/**
 * Translates a JS date into a timezone-aware date string.
 * @param date the date to convert to a day in a timezone
 * @param tz the timezone to convert the date to (e.g. "America/Los_Angeles")
 * @returns the day of the date in the timezone, in ISO format (e.g. "2024-01-01")
 */
export function dayInZone(date: Date, tz: string) {
  return DateTime.fromJSDate(date).setZone(tz, { keepLocalTime: true }).toISODate()
}

/** Vainilla list of month names */
export const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
